import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import defaultShield from '@sorare/core/src/assets/club/shield_none.webp';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  BodyS,
  LabelL,
  LabelM,
} from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { isType } from '@sorare/core/src/lib/gql';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import type { Color } from '@sorare/core/src/style/types';
import { css } from '@sorare/css';

import { DomesticLeagueRanking } from '#src/shared/game/DomesticLeagueRanking/index.tsx';
import { GameStatusLabel } from '#src/shared/game/GameStatusLabel/index.tsx';
import { isGameScheduled, isGameStarted } from '#src/shared/game/lib.tsx';
import { HandInfoChip } from '#src/shared/player/HandInfoChip/index.tsx';

import { TeamWinOdds } from './TeamWinOdds/index.tsx';
import type {
  BriefGame_anyGameInterface,
  BriefGame_teamInterface,
} from './__generated__/index.graphql.ts';

const root = css`
  width: 100%;
  text-align: left;
  cursor: default;
  background: var(--c-surface-primary);
  border-radius: var(--double-unit);
  padding: var(--double-unit);

  @media ${tabletAndAbove} {
    &.withHoverStyles {
      padding: var(--double-unit);
      cursor: pointer;
      border-radius: var(--double-unit);

      &:hover {
        background-color: var(--c-surface-primary);
      }
    }
  }
`;

const getTeamTextColor = (
  game: BriefGame_anyGameInterface,
  teamSlug?: string
): Color => {
  return !game.winner || game.winner?.slug === teamSlug
    ? 'var(--c-white)'
    : 'var(--c-nd-400)';
};

// Both teams' stats carry the same market, so the away win is the home side's loss.
const findWinOdds = (game: BriefGame_anyGameInterface) => {
  const { homeStats } = game;

  if (
    !isGameScheduled(game.status) ||
    !isType(homeStats, 'FootballTeamGameStats')
  ) {
    return null;
  }

  const { winOddsBasisPoints, loseOddsBasisPoints } = homeStats;
  if (winOddsBasisPoints === null || loseOddsBasisPoints === null) {
    return null;
  }

  return { home: winOddsBasisPoints, away: loseOddsBasisPoints };
};

const LOGO_SIZE = '16px';

type Props = {
  game: BriefGame_anyGameInterface;
  team?: BriefGame_teamInterface;
  onClick?: () => void;
};
export const BriefGame = ({ game, team, onClick }: Props) => {
  const { sport } = useSportContext();
  const hasGameStarted = isGameStarted(game.status);
  const isUsSports = sport !== Sport.FOOTBALL;

  const { homeTeam, homeScore, awayTeam, awayScore } = game;

  const isBaseballGame = isType(game, 'GameOfBaseball');

  // respect logic above, fake the homePitcher is awayPitcher and vice versa

  const [awayPitcher, homePitcher] = isBaseballGame
    ? [game.awayProbablePitcher, game.homeProbablePitcher]
    : [];
  const useTeamShortName = isBaseballGame;

  const winOdds = findWinOdds(game);

  const content = (
    <Vertical gap={1.5}>
      <LabelM bold color="var(--c-onBase-secondary)">
        <GameStatusLabel game={game} withDateWhenFinished />
      </LabelM>
      <Vertical
        gap={1}
        className={classNames({ 'flex-col-reverse': isUsSports })}
      >
        <SBHorizontal>
          <Vertical gap={0.5} className="max-w-8/10">
            <Horizontal>
              <img
                className="w-2 object-contain"
                src={homeTeam?.pictureUrl || defaultShield}
                alt=""
                width={LOGO_SIZE}
                height={LOGO_SIZE}
              />
              <Horizontal align="baseline" className="overflow-hidden">
                <LabelL
                  className="truncate"
                  bold
                  color={getTeamTextColor(game, homeTeam?.slug)}
                >
                  {useTeamShortName ? homeTeam?.shortName : homeTeam?.name}
                </LabelL>
                {!!homeTeam && (
                  <LabelM>
                    <DomesticLeagueRanking
                      game={game}
                      team={homeTeam}
                      colored={homeTeam.slug !== team?.slug}
                    />
                  </LabelM>
                )}
              </Horizontal>
            </Horizontal>
            {!!isBaseballGame && !!homePitcher && (
              <Horizontal className="ml-3">
                <BodyS as="span">{homePitcher.displayName}</BodyS>
                <HandInfoChip position="pitching" player={homePitcher} />
              </Horizontal>
            )}
          </Vertical>
          {winOdds ? (
            <TeamWinOdds winOddsBasisPoints={winOdds.home} />
          ) : (
            <BodyM
              as="p"
              bold
              color={
                hasGameStarted
                  ? getTeamTextColor(game, homeTeam?.slug)
                  : 'var(--c-onBase-secondary)'
              }
            >
              {hasGameStarted ? homeScore : '-'}
            </BodyM>
          )}
        </SBHorizontal>
        <SBHorizontal>
          <Vertical gap={0.5} className="max-w-8/10">
            <Horizontal>
              <img
                className="w-2 object-contain"
                src={awayTeam?.pictureUrl || defaultShield}
                alt=""
                width={LOGO_SIZE}
                height={LOGO_SIZE}
              />
              <Horizontal align="baseline" className="overflow-hidden">
                <LabelL
                  className="truncate"
                  bold
                  color={getTeamTextColor(game, awayTeam?.slug)}
                >
                  {useTeamShortName ? awayTeam?.shortName : awayTeam?.name}
                </LabelL>
                {!!awayTeam && (
                  <LabelM>
                    <DomesticLeagueRanking
                      game={game}
                      team={awayTeam}
                      colored={awayTeam.slug !== team?.slug}
                    />
                  </LabelM>
                )}
              </Horizontal>
            </Horizontal>
            {!!isBaseballGame && !!awayPitcher && (
              <Horizontal className="ml-3">
                <BodyS as="span">{awayPitcher.displayName}</BodyS>
                <HandInfoChip position="pitching" player={awayPitcher} />
              </Horizontal>
            )}
          </Vertical>
          {winOdds ? (
            <TeamWinOdds winOddsBasisPoints={winOdds.away} />
          ) : (
            <BodyM
              as="p"
              bold
              color={
                hasGameStarted
                  ? getTeamTextColor(game, awayTeam?.slug)
                  : 'var(--c-onBase-secondary)'
              }
            >
              {hasGameStarted ? awayScore : '-'}
            </BodyM>
          )}
        </SBHorizontal>
      </Vertical>
    </Vertical>
  );

  return onClick ? (
    <ButtonBase
      className={classNames(root, 'withHoverStyles')}
      onClick={onClick}
    >
      {content}
    </ButtonBase>
  ) : (
    <div className={root}>{content}</div>
  );
};

const teamFragment = gql`
  fragment BriefGame_teamInterface on TeamInterface {
    slug
    name
    shortName
    pictureUrl
    ... on NationalTeam {
      slug
    }
    ...DomesticLeagueRanking_teamInterface
  }
  ${DomesticLeagueRanking.fragments.teamInterface}
` as TypedDocumentNode<BriefGame_teamInterface>;

BriefGame.fragments = {
  anyGameInterface: gql`
    fragment BriefGame_anyGameInterface on AnyGameInterface {
      id
      status: statusTyped
      homeScore
      homeTeam {
        slug
        ...BriefGame_teamInterface
      }
      awayScore
      awayTeam {
        slug
        ...BriefGame_teamInterface
      }
      winner {
        slug
      }
      homeStats {
        id
        ... on FootballTeamGameStats {
          id
          winOddsBasisPoints
          loseOddsBasisPoints
        }
      }
      ... on GameOfBaseball {
        id
        homeProbablePitcher {
          slug
          displayName
          ...HandInfoChip_BaseballPlayer
        }
        awayProbablePitcher {
          slug
          displayName
          ...HandInfoChip_BaseballPlayer
        }
      }
      ...GameStatusLabel_anyGameInterface
      ...DomesticLeagueRanking_anyGameInterface
    }
    ${GameStatusLabel.fragments.anyGameInterface}
    ${DomesticLeagueRanking.fragments.anyGameInterface}
    ${teamFragment}
    ${HandInfoChip.fragments.BaseballPlayer}
  ` as TypedDocumentNode<BriefGame_anyGameInterface>,
  teamInterface: teamFragment,
};
